import { request } from '../request';

import i18n from '../../i18n';

export type UserFeedback = 'positive' | 'negative' | 'neutral';

export interface ChatMessage {
  content: string;
  sender: 'user' | 'model';
}

export interface AiQueryRequest {
  conversation_id: number;
  query: string;
  query_context: {
    chat_history: ChatMessage[];
    [key: string]: any;
  };
}

export interface AiConversation {
  id: number;
}

export type ChatHistory = AiQueryRequest['query_context']['chat_history'];

export interface AiQueryResponse {
  id: number;
  answer: string;
}

function instanceOfAiQueryResponse(object: any): object is AiQueryResponse {
  return 'answer' in object;
}

/**
 * Processes a natural language query about trial data using AI
 *
 * @param data Query and context for the AI to process
 * @returns Promise with the AI response
 */
export async function processAiQuery(data: AiQueryRequest): Promise<AiQueryResponse> {
  const response = await request<AiQueryResponse>('POST', '/api/v2/ai/messages/', data, {
    injectTenant: true,
    version: 2,
    skipErrorResponseHandling: true,
  });

  if (!instanceOfAiQueryResponse(response)) {
    // Response is empty when an error occurs. That is handled by the request function.
    return {
      id: null,
      answer: i18n.t(
        "I'm sorry, I encountered an error while processing your request. Please try again later."
      )(),
    };
  }

  return response;
}

export async function createConversation(trialId: number): Promise<AiConversation> {
  const data = {
    trial_id: trialId,
  };

  const response = await request<AiConversation>('POST', '/api/v2/ai/conversations/', data, {
    injectTenant: true,
    version: 2,
    skipErrorResponseHandling: true,
  });

  return response;
}

export async function sendMessageFeedback(messageId: number, feedback: UserFeedback | null): Promise<void> {
  const data = {
    feedback,
  };

  await request('PATCH', `/api/v2/ai/messages/${messageId}/`, data, {
    injectTenant: true,
    version: 2,
    skipErrorResponseHandling: true,
  });
}
